import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { IconButton } from './Button';
import styles from './modal.module.css';

/**
 * A modal dialog on the native `<dialog>` element — the first modal in the app (the add-to-playlist
 * panel is its first caller). Native rather than hand-rolled because the element already does the
 * hard parts correctly: `showModal()` traps focus, puts the dialog in the top layer above every
 * `z-index` in the token scale, makes the rest of the page inert, and wires Escape to `cancel`.
 *
 * The caller owns `open` the way it owns a `Menu`'s: the element's own close paths (Escape) report
 * back through `onClose` rather than leaving the two out of step. A click that lands on the element
 * itself is a click on the backdrop — the frame inside covers everything else — and closes too.
 */
export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The heading, and the dialog's accessible name. */
  readonly title: string;
  /** The × button's accessible name — the caller's words (R7). */
  readonly closeLabel: string;
  readonly children: ReactNode;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        d="M6 6l12 12M18 6L6 18"
      />
    </svg>
  );
}

export function Modal({ open, onClose, title, closeLabel, children }: ModalProps) {
  if (!open) return null;

  const onBackdropClick = (event: ReactMouseEvent<HTMLDialogElement>): void => {
    // Only the backdrop dispatches to the dialog element itself; every real target is in the frame.
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <dialog
      className={styles.modal}
      // Mounted only while open, so the ref callback is the mount hook: `showModal()` (not `show()`,
      // not the `open` attribute) is what buys the top layer, the focus trap and the inert page.
      ref={(element) => {
        if (element !== null && !element.open) element.showModal();
      }}
      aria-label={title}
      onClose={onClose}
      onClick={onBackdropClick}
    >
      <div className={styles.frame}>
        <header className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          <IconButton variant="subtle" label={closeLabel} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </header>
        {children}
      </div>
    </dialog>
  );
}
